import { HttpClient } from "@angular/common/http";
import { Injectable, NgZone, inject } from "@angular/core";
import { Capacitor } from "@capacitor/core";
import { AlertLike, AlertNavigationService } from "./alert-navigation.service";
import {
  ActionPerformed,
  PushNotificationSchema,
  PushNotifications,
  Token,
} from "@capacitor/push-notifications";
import { environment } from "src/environments/environment";
import { Observable, of } from "rxjs";
import { catchError } from "rxjs/operators";

/**
 * Notificacoes push (FCM) do visual APP.
 *
 * Fluxo: no bootstrap (plataforma nativa) pedimos permissao e registramos no
 * FCM. Ao receber o token, enviamos para o backend (POST device-token/) desde
 * que o usuario esteja autenticado — caso contrario o token fica guardado e e
 * sincronizado no login (LoginPage.onSubmit chama syncToken()).
 *
 * O backend dispara um push para cada Alerta criado (signal post_save), entao
 * todas as notificacoes in-app viram push automaticamente.
 */
@Injectable({ providedIn: "root" })
export class PushService {
  private http = inject(HttpClient);
  private nav = inject(AlertNavigationService);
  private zone = inject(NgZone);

  private currentToken: string | null = null;
  private initialized = false;

  /** Chamar uma vez no bootstrap do app. No-op fora de plataforma nativa. */
  async init(): Promise<void> {
    if (this.initialized) return;
    if (!Capacitor.isNativePlatform()) return;
    this.initialized = true;

    // Registra listeners ANTES de register() para nao perder eventos.
    await PushNotifications.addListener("registration", (token: Token) => {
      this.currentToken = token.value;
      this.syncToken();
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.error("[PUSH] erro no registro FCM:", err);
    });

    await PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotificationSchema) => {
        // App em foreground: o Android nao exibe automaticamente. Apenas log
        // por enquanto — o badge/sino in-app continua atualizando via polling.
        console.log("[PUSH] recebida (foreground):", notification?.title);
      },
    );

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action: ActionPerformed) => {
        this.zone.run(() => this.handleTap(action?.notification?.data));
      },
    );

    const perm = await PushNotifications.requestPermissions();
    if (perm.receive === "granted") {
      await PushNotifications.register();
    } else {
      console.warn("[PUSH] permissao de notificacao negada pelo usuario");
    }
  }

  /** Envia o token atual ao backend. Só age se houver token e sessao ativa. */
  syncToken(): void {
    if (!this.currentToken) return;
    if (localStorage.getItem("authenticated") !== "true") return;

    const url =
      environment.api.baseUrl + environment.api.endpoints.register_device_token;
    this.http
      .post(url, {
        token: this.currentToken,
        platform: Capacitor.getPlatform(),
      })
      .subscribe({
        next: () => console.log("[PUSH] token registrado no backend"),
        error: (e) => console.error("[PUSH] falha ao registrar token:", e),
      });
  }

  /**
   * Desativa o token no backend (chamar no logout). No-op se sem token.
   *
   * Devolve o observable em vez de assinar: o logout so pode revogar a sessao
   * depois deste DELETE chegar, senao o proprio DELETE sai com token morto.
   */
  removeToken(): Observable<unknown> {
    if (!this.currentToken) return of(null);
    const url =
      environment.api.baseUrl + environment.api.endpoints.register_device_token;
    return this.http
      .request("delete", url, { body: { token: this.currentToken } })
      .pipe(catchError(() => of(null)));
  }

  /**
   * Navegacao ao tocar na notificacao.
   *
   * Delega ao mesmo servico que a lista de avisos usa, entao push e aviso levam
   * sempre ao mesmo lugar. O backend manda titulo/prof_id/role no data
   * justamente porque sao os campos que essa decisao consome.
   */
  private handleTap(data: Record<string, unknown> | undefined): void {
    this.nav.navigateForAlert(data as AlertLike | undefined);
  }
}
